import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { FormActionBar, WindowContent, toast } from '@/shared/ui';
import { cn } from '@/shared/lib/utils';
import { useNavigator } from '@/shared/navigation/react/useNavigator';
import { getGetProductsStationChainQueryKey } from '@/api/generated/products-station-chains/products-station-chains';
import { STATION_CHAIN_LIST_KEY } from '../internal/stationChainsQueryKeys';
import { useStationChainForm } from './useStationChainForm';
import { useStationChainEditor } from './useStationChainEditor';
import { StationChainIdentityPanel } from './components/StationChainIdentityPanel';
import { StopsPanel } from './components/StopsPanel';
import { StationChainMapPanel } from './components/StationChainMapPanel';
import type { StationChainFormValues } from './stationChainFormSchema';

export interface StationChainDetailViewProps {
  layerId: string;
  stationChainId?: string;
  initialValues: StationChainFormValues;
}

/**
 * Orchestrátor detailu trasy — drží formulář (`useStationChainForm`) a editor zastávek
 * (`useStationChainEditor`), skládá panely (identita + zastávky + mapa) a sdílenou lištu akcí v patičce.
 */
export function StationChainDetailView({
  layerId,
  stationChainId,
  initialValues,
}: StationChainDetailViewProps) {
  const { t } = useTranslation('products');
  const navigator = useNavigator();
  const queryClient = useQueryClient();
  const isEdit = stationChainId != null;

  const close = useCallback(() => {
    void navigator.closeLayer(layerId);
  }, [navigator, layerId]);

  const { form, submit } = useStationChainForm({
    stationChainId,
    initialValues,
    onSaved: (id) => {
      // Detail (Orval klíč) — aby se po znovuotevření načetla čerstvá data; seznam (prefix klíče
      // ListPage configu). Viz `frontend/GUIDELINES.md` → „Detail stránky — čerstvost dat".
      void queryClient.invalidateQueries({ queryKey: getGetProductsStationChainQueryKey(id) });
      void queryClient.invalidateQueries({ queryKey: [STATION_CHAIN_LIST_KEY] });
      toast({ severity: 'success', description: t('stationChain.toast.saved') });
    },
    onError: () => {
      toast({
        severity: 'error',
        title: t('stationChain.toast.errorTitle'),
        description: t('stationChain.toast.saveError'),
      });
    },
  });

  const editor = useStationChainEditor(form);

  const title = isEdit ? t('stationChain.titleEdit') : t('stationChain.titleCreate');

  return (
    <WindowContent
      as="form"
      scroll={false}
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
      footer={
        <form.Subscribe
          selector={(state) => ({
            isDirty: state.isDirty,
            canSubmit: state.canSubmit,
            isSubmitting: state.isSubmitting,
          })}
        >
          {({ isDirty, canSubmit, isSubmitting }) => (
            <FormActionBar
              onBack={close}
              onSave={submit}
              saving={isSubmitting}
              disabled={!canSubmit}
              statusNote={
                <>
                  <span
                    className={cn(
                      'mr-phx-2 inline-block h-1.5 w-1.5 rounded-full',
                      isDirty ? 'bg-phx-warning' : 'bg-phx-text-faint'
                    )}
                  />
                  {isDirty ? t('stationChain.status.unsaved') : t('stationChain.status.clean')}
                </>
              }
            />
          )}
        </form.Subscribe>
      }
    >
      {/* Vlastní 2-sloupcový split ve fill oblasti okna */}
      <div className="gap-phx-3 flex h-full min-h-0">
        {/* Detail (~500px) */}
        <div className="gap-phx-3 flex min-h-0 w-[500px] shrink-0 flex-col overflow-y-auto">
          <StationChainIdentityPanel form={form} title={title} />
          <StopsPanel form={form} editor={editor} />
        </div>

        {/* Mapa (zbytek šířky) */}
        <StationChainMapPanel form={form} editor={editor} />
      </div>
    </WindowContent>
  );
}
